import React from "react";

import { GetStaticProps } from "next";
import {
  ATypeCardView,
  ATypeNationalMeritHead,
  BTypeBreadCrumb,
  FullWidthTwinButton,
  call,
  ATypeNationalMeritSummary,
  nationalMeritRichSearch,
  RichSearch,
  GOI_WEB_URL,
  customEvent,
} from "goi_common";
import crematorium from "goi_common/src/constants/hwajang_detail.json";
import facilities from "goi_common/src/constants/facilities_detail.json";
import parlors from "goi_common/src/constants/parlors_detail.json";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos, bestParlorIds } = parlorRegion;

export const getStaticProps: GetStaticProps = async () => {
  const regionCrematoriums = crematorium.filter((p) =>
    targetSidos.includes(String(p.sido))
  );
  const regionPublicFacilities = facilities.filter(
    (f) => targetSidos.includes(f.sido) && f.is_public
  );
  const regionBestParlors = parlors.filter((p) => bestParlorIds.includes(p.id));
  const description =
    "대전충청 국가유공자 장례 준비. 대전 국가유공자 장례 준비, 충청 국가유공자 장례 준비, 대전충청 국가유공자 장례 상담, 대전 국가유공자 장례, 충청 국가유공자 장례, 대전 국가유공자 장례 혜택, 충청 국가유공자 장례 혜택";

  return {
    props: {
      regionCrematoriums,
      regionPublicFacilities,
      regionBestParlors,
      pageDescription: description,
      pageKeywords: `국가유공자 장례 준비, 대전 국가유공자 장례 혜택, 충청 국가유공자 장례 혜택, 대전충청 국가유공자 장례 해택, 국가유공자 장례 비용`,
      pageOgTitle: `국가유공자 장례 114 :: 대전충청장례정보포털`,
      pageOgDescription: description,
      pageOgImage: "",
    },
  };
};

export default function index({
  regionCrematoriums,
  regionPublicFacilities,
  regionBestParlors,
  pageOgTitle,
}: {
  regionCrematoriums: Array<any>;
  regionPublicFacilities: Array<any>;
  regionBestParlors: Array<any>;
  pageOgTitle: string;
}) {
  const { faqList } = nationalMeritRichSearch;
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        <RichSearch data={faqList} />
      </Head>
      <BTypeBreadCrumb
        firstItem={{
          text: `국가유공자 장례 추천`,
          href: GOI_WEB_URL + "/match/request/",
          event: {
            domain: "ch",
            path: "nat_hr",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `국가유공자 장례114`,
          href: `/national-hero/`,
          event: {
            domain: "ch",
            path: "nat_hr",
            trigger_and_target: "br_crumb_nat_hr",
          },
        }}
        goiHomeEvent={{
          domain: "ch",
          path: "nat_hr",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <Box
        style={{ backgroundColor: regionColor.c100, padding: "20px 0px 0px" }}
      >
        <ATypeNationalMeritHead
          titleSido="충청 · 대전 · 세종"
          mainColor={regionColor}
          title="국가유공자 장례114"
        />
      </Box>
      <Stack padding="20px 20px 40px" gap="24px">
        <FullWidthTwinButton
          firstBtn={{
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "ch",
                path: "nat_hr",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: GOI_WEB_URL + "/match/request/",
            onClick: () => {
              customEvent({
                domain: "ch",
                path: "nat_hr",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
      </Stack>

      <Stack padding="40px 20px" marginBottom="40px" gap="8px">
        <h3 className="button_16_sb">충청 대전 세종 지역 장례식장</h3>
        {regionBestParlors.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`https://www.goifuneral.co.kr/match/parlor-request/${res.companyname}/`}
            descriptions={[
              `빈소 평균 비용 : ${Number(
                res.binso_average_charge
              ).toLocaleString()}원`,
            ]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            arrowText="혜택 알아보기"
            event={{
              domain: "ch",
              path: "nat_hr",
              trigger_and_target: "uv_prlr_req",
            }}
          />
        ))}
      </Stack>

      <Stack
        padding="24px 16px 10px"
        gap="12px"
        style={{ background: regionColor.c100 }}
      >
        <h3 className="subtitle_18_b">충청 대전 세종 국가유공자 무료 화장장</h3>
        {regionCrematoriums.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`/national-hero/free-crema/${res.companyname} 국가유공자 안내/`}
            descriptions={["국가유공자 무료"]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "ch",
              path: "nat_hr",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>

      <Stack padding="24px 16px 10px" gap="12px">
        <h3 className="subtitle_18_b">
          충청 대전 세종 국가유공자 무료 장지 (공설)
        </h3>
        {regionPublicFacilities.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`/national-hero/free-facility/${res.companyname} 국가유공자 안내/`}
            descriptions={["국가유공자 우선 안치"]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "ch",
              path: "nat_hr",
              trigger_and_target: "uv_facility_detail",
            }}
          />
        ))}
      </Stack>
    </>
  );
}
